import { useState, useEffect } from "react";
import styled from "styled-components";
import { Badge, Tooltip, Whisper } from "rsuite";
import { useAuth0 } from "@auth0/auth0-react";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import FolderFillIcon from "@rsuite/icons/FolderFill";
import UsersIcon from "@rsuite/icons/Peoples";
import LocationIcon from "@rsuite/icons/Location";
import PcIcon from "@rsuite/icons/Pc";
import { SectionRibbon, Card, NoDataState, Loader } from "../../../../ui/";
import { colors } from "../../../../ui/theme/colors";
import { CreateSiteGroup } from "./Modal/CreateSiteGroup";
import { EditSiteGroup } from "./Modal/EditSiteGroup";
import { DeleteSiteGroup } from "./Modal/DeleteSiteGroup";
import { ManageSitesInGroup } from "./Modal/ManageSitesInGroup";

export const CustomerSiteGroupsFC = (props) => {
  const [siteGroups, setSiteGroups] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteGroupMappings, setSiteGroupMappings] = useState([]);
  const [siteGroupUsers, setSiteGroupUsers] = useState([]);
  const [update, setUpdate] = useState(false);
  const [loadingSiteGroups, setLoadingSiteGroups] = useState(true);
  const [loadingSites, setLoadingSites] = useState(true);
  const [loadingMappings, setLoadingMappings] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [error, setError] = useState(null);
  const { getAccessTokenSilently } = useAuth0();

  const fetchAllData = async () => {
    setLoadingSiteGroups(true);
    setLoadingSites(true);
    setLoadingMappings(true);
    setLoadingUsers(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const headers = { authorization: `Bearer ${token}` };

      // Fetch all data concurrently for better performance
      const [siteGroupsRes, sitesRes, mappingsRes, usersRes] =
        await Promise.allSettled([
          axiosInstance({
            url: `/customer/site-groups/${props.customer}`,
            data: ``,
            headers,
          }),
          axiosInstance({
            url: `/customer/sites/${props.customer}`,
            data: ``,
            headers,
          }),
          axiosInstance({
            url: `/customer/site-group-mappings/${props.customer}`,
            data: ``,
            headers,
          }),
          axiosInstance({
            url: `/admin/customer-site-group-users/${props.customer}`,
            data: ``,
            headers,
          }),
        ]);

      // Handle site groups response
      // console.log("DEBUG: Site groups raw response:", siteGroupsRes);
      if (siteGroupsRes.status === "fulfilled") {
        // console.log("DEBUG: Site groups data:", siteGroupsRes.value?.data);
        // console.log(
        //   "DEBUG: Site groups data[0]:",
        //   siteGroupsRes.value?.data[0],
        // );
        const processedSiteGroups = siteGroupsRes.value?.data[0] || [];
        // console.log("DEBUG: Processed site groups:", processedSiteGroups);
        setSiteGroups(processedSiteGroups);
      } else {
        console.error("Error fetching site groups:", siteGroupsRes.reason);
        setSiteGroups([]);
      }

      // Handle sites response
      if (sitesRes.status === "fulfilled") {
        setSites(sitesRes.value?.data[0] || []);
      } else {
        console.error("Error fetching sites:", sitesRes.reason);
        setSites([]);
      }

      // Handle mappings response
      // console.log("DEBUG: Site group mappings raw response:", mappingsRes);
      if (mappingsRes.status === "fulfilled") {
        // console.log(
        //   "DEBUG: Site group mappings data:",
        //   mappingsRes.value?.data,
        // );
        const processedMappings = mappingsRes.value?.data[0] || [];
        // console.log("DEBUG: Processed mappings:", processedMappings);
        setSiteGroupMappings(processedMappings);
      } else {
        console.error(
          "Error fetching site group mappings:",
          mappingsRes.reason,
        );
        setSiteGroupMappings([]);
      }

      // Handle users response
      // console.log("DEBUG: Site group users raw response:", usersRes);
      if (usersRes.status === "fulfilled") {
        // console.log("DEBUG: Site group users data:", usersRes.value?.data);
        const processedUsers = usersRes.value?.data[0] || [];
        // console.log("DEBUG: Processed users:", processedUsers);
        setSiteGroupUsers(processedUsers);
      } else {
        console.error("Error fetching site group users:", usersRes.reason);
        setSiteGroupUsers([]);
      }

      // Set error if any request failed
      const failedRequests = [
        siteGroupsRes,
        sitesRes,
        mappingsRes,
        usersRes,
      ].filter((res) => res.status === "rejected");

      if (failedRequests.length > 0) {
        setError(
          `Failed to load ${failedRequests.length} data source${failedRequests.length > 1 ? "s" : ""}`,
        );
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load data. Please try again.");
      setSiteGroups([]);
      setSites([]);
      setSiteGroupMappings([]);
      setSiteGroupUsers([]);
    } finally {
      setLoadingSiteGroups(false);
      setLoadingSites(false);
      setLoadingMappings(false);
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    // console.log(
    //   "DEBUG: useEffect triggered with customer:",
    //   props.customer,
    //   "update:",
    //   update,
    // );
    if (props.customer) {
      fetchAllData();
    }
  }, [props.customer, update]);

  // Debug effect to log state changes
  useEffect(() => {
    // console.log("DEBUG: Site groups state updated:", siteGroups);
    // console.log("DEBUG: Site groups length:", siteGroups?.length);
  }, [siteGroups]);

  const getSitesInGroup = (groupId) => {
    const siteIdsInGroup = siteGroupMappings
      .filter((mapping) => mapping.site_group_id === groupId)
      .map((mapping) => mapping.site_id);
    return sites.filter((site) => siteIdsInGroup.includes(site.id));
  };

  const getUnassignedSites = () => {
    const assignedSiteIds = siteGroupMappings.map((mapping) => mapping.site_id);
    return sites.filter((site) => !assignedSiteIds.includes(site.id));
  };

  const getGroupsForSite = (siteId) => {
    const groupIdsForSite = siteGroupMappings
      .filter((mapping) => mapping.site_id === siteId)
      .map((mapping) => mapping.site_group_id);
    return siteGroups.filter((group) => groupIdsForSite.includes(group.id));
  };

  const getUsersInSiteGroup = (groupId) => {
    return siteGroupUsers.filter(
      (userAssignment) => userAssignment.site_group_id === groupId,
    );
  };

  const getUserCountForSiteGroup = (groupId) => {
    const users = getUsersInSiteGroup(groupId);
    return users.length;
  };

  const getUserDisplayName = (userAssignment) => {
    const { first_name, last_name, user_email } = userAssignment;
    if (first_name && last_name) {
      return `${first_name} ${last_name}`;
    } else if (first_name) {
      return first_name;
    } else if (last_name) {
      return last_name;
    } else {
      return user_email || "Unknown User";
    }
  };

  const formatLastModified = (group) => {
    const { updated_at } = group;
    if (!updated_at) return "Never";

    const date = new Date(updated_at);
    const formattedDate = date.toLocaleDateString();
    const formattedTime = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    return (
      <div>
        <div style={{ fontSize: "12px", fontWeight: "bold" }}>
          {formattedDate}
        </div>
        <div style={{ fontSize: "11px", color: "#666" }}>{formattedTime}</div>
      </div>
    );
  };

  const handleMappingsUpdate = (updatedMappings) => {
    // Update the siteGroupMappings state
    setSiteGroupMappings(updatedMappings);
  };

  const refreshSiteGroupsOnly = async () => {
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const headers = { authorization: `Bearer ${token}` };

      const siteGroupsRes = await axiosInstance({
        url: `/customer/site-groups/${props.customer}`,
        data: ``,
        headers,
      });

      if (siteGroupsRes?.data[0]) {
        setSiteGroups(siteGroupsRes.data[0]);
      }
    } catch (error) {
      console.error("Error refreshing site groups:", error);
    }
  };

  const isLoading =
    loadingSiteGroups || loadingSites || loadingMappings || loadingUsers;

  if (isLoading) {
    return (
      <Container>
        <Loader
          content="Loading Site Groups..."
          subtext="Please wait while we fetch site group information"
          centered={true}
          size="md"
        />
      </Container>
    );
  }

  return (
    <Container>
      <SectionRibbon
        title="Site Groups"
        subtitle="Organize sites into manageable groups for better access control"
        icon={FolderFillIcon}
        variant="primary"
        rightContent={
          <StatsContainer>
            <StatItem>
              <StatValue>{siteGroups?.length || 0}</StatValue>
              <StatLabel>Total Groups</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue>{getUnassignedSites()?.length || 0}</StatValue>
              <StatLabel>Unassigned Sites</StatLabel>
            </StatItem>
            <CreateButtonContainer>
              <CreateSiteGroup
                customer={props.customer}
                update={update}
                setUpdate={setUpdate}
              />
            </CreateButtonContainer>
          </StatsContainer>
        }
      />

      {error && (
        <ErrorContainer>
          <ErrorText>⚠️ {error}</ErrorText>
          <RetryButton onClick={fetchAllData}>Retry</RetryButton>
        </ErrorContainer>
      )}

      {siteGroups.length > 0 ? (
        <SiteGroupsGrid>
          {siteGroups?.map((group, index) => {
            const groupSites = getSitesInGroup(group.id);
            const totalSystems = groupSites.reduce(
              (acc, site) => acc + parseInt(site.systems || 0),
              0,
            );
            const userCount = getUserCountForSiteGroup(group.id);
            const usersInGroup = getUsersInSiteGroup(group.id);

            return (
              <Card
                key={index}
                variant="default"
                style={{
                  animationDelay: `${index * 0.1}s`,
                  animation: `fadeInUp 0.6s ease-out forwards`,
                  opacity: 0,
                }}
              >
                <GroupContent>
                  <GroupHeader>
                    <GroupTitleSection>
                      <GroupIcon>
                        <FolderFillIcon />
                      </GroupIcon>
                      <GroupInfo>
                        <GroupName>{group.name}</GroupName>
                        {userCount > 0 && (
                          <Whisper
                            trigger="hover"
                            placement="topStart"
                            speaker={
                              <Tooltip>
                                <UserTooltipContent>
                                  <TooltipTitle>
                                    Users in this site group:
                                  </TooltipTitle>
                                  <UserList>
                                    {usersInGroup
                                      .slice(0, 5)
                                      .map((userAssignment, idx) => (
                                        <UserListItem key={idx}>
                                          • {getUserDisplayName(userAssignment)}
                                        </UserListItem>
                                      ))}
                                    {usersInGroup.length > 5 && (
                                      <UserListItem>
                                        ... and {usersInGroup.length - 5} more
                                      </UserListItem>
                                    )}
                                  </UserList>
                                </UserTooltipContent>
                              </Tooltip>
                            }
                          >
                            <UserBadge>
                              <UsersIcon
                                style={{ fontSize: "12px", marginRight: "4px" }}
                              />
                              {userCount} {userCount === 1 ? "user" : "users"}
                            </UserBadge>
                          </Whisper>
                        )}
                      </GroupInfo>
                    </GroupTitleSection>

                    <ActionButtons>
                      <ManageSitesInGroup
                        group={group}
                        sites={sites}
                        siteGroupMappings={siteGroupMappings}
                        customer={props.customer}
                        update={update}
                        setUpdate={setUpdate}
                        onMappingsUpdate={handleMappingsUpdate}
                        refreshSiteGroups={refreshSiteGroupsOnly}
                      />
                      <EditSiteGroup
                        group={group}
                        update={update}
                        setUpdate={setUpdate}
                      />
                      <DeleteSiteGroup
                        group={group}
                        update={update}
                        setUpdate={setUpdate}
                      />
                    </ActionButtons>
                  </GroupHeader>

                  <GroupStats>
                    <StatCard>
                      <StatIcon>
                        <LocationIcon />
                      </StatIcon>
                      <StatInfo>
                        <StatNumber>{groupSites.length}</StatNumber>
                        <StatLabel>Sites</StatLabel>
                      </StatInfo>
                    </StatCard>

                    <StatCard>
                      <StatIcon>
                        <PcIcon />
                      </StatIcon>
                      <StatInfo>
                        <StatNumber>{totalSystems}</StatNumber>
                        <StatLabel>Systems</StatLabel>
                      </StatInfo>
                    </StatCard>

                    <StatCard>
                      <LastModifiedContainer>
                        <LastModifiedLabel>Last Modified</LastModifiedLabel>
                        <LastModifiedValue>
                          {formatLastModified(group)}
                        </LastModifiedValue>
                      </LastModifiedContainer>
                    </StatCard>
                  </GroupStats>
                </GroupContent>
              </Card>
            );
          })}
        </SiteGroupsGrid>
      ) : (
        <NoDataState
          icon="📁"
          title="No Site Groups"
          message="No site groups created yet. Click the 'Create Group' button to get started."
        />
      )}

      {getUnassignedSites().length > 0 && (
        <UnassignedSection>
          <SectionHeader>
            <SectionTitle>Unassigned Sites</SectionTitle>
            <SectionSubtitle>
              These sites are not assigned to any group. Use the "Manage Sites"
              action to assign them.
            </SectionSubtitle>
          </SectionHeader>
          <UnassignedSitesGrid>
            {getUnassignedSites().map((site, index) => (
              <Card key={index} variant="warning">
                <UnassignedSiteContent>
                  <SiteInfo>
                    <SiteNameUnassigned>{site.name}</SiteNameUnassigned>
                    <SiteLocation>
                      {site.city}, {site.state}
                    </SiteLocation>
                  </SiteInfo>
                  <SystemsBadge>
                    <PcIcon style={{ fontSize: "14px", marginRight: "4px" }} />
                    {site.systems} systems
                  </SystemsBadge>
                </UnassignedSiteContent>
              </Card>
            ))}
          </UnassignedSitesGrid>
        </UnassignedSection>
      )}

      {sites.length > 0 && siteGroupMappings.length > 0 && (
        <AssignedSection>
          <SectionHeader>
            <SectionTitle>Site Group Assignments</SectionTitle>
            <SectionSubtitle>
              View which groups each site belongs to.
            </SectionSubtitle>
          </SectionHeader>
          <AssignedSitesGrid>
            {sites
              .filter((site) => {
                const assignedSiteIds = siteGroupMappings.map(
                  (mapping) => mapping.site_id,
                );
                return assignedSiteIds.includes(site.id);
              })
              .map((site, index) => {
                const siteGroups = getGroupsForSite(site.id);
                return (
                  <Card key={index} variant="info">
                    <AssignedSiteContent>
                      <SiteInfoSection>
                        <SiteNameAssigned>{site.name}</SiteNameAssigned>
                        <SiteLocationAssigned>
                          {site.city}, {site.state}
                        </SiteLocationAssigned>
                        <GroupTags>
                          {siteGroups.map((group) => (
                            <GroupTag key={group.id}>{group.name}</GroupTag>
                          ))}
                        </GroupTags>
                      </SiteInfoSection>
                      <SystemsBadge>
                        <PcIcon
                          style={{ fontSize: "14px", marginRight: "4px" }}
                        />
                        {site.systems} systems
                      </SystemsBadge>
                    </AssignedSiteContent>
                  </Card>
                );
              })}
          </AssignedSitesGrid>
        </AssignedSection>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const StatValue = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.neutral[800]};
`;

const StatLabel = styled.span`
  font-size: 12px;
  color: ${colors.neutral[500]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CreateButtonContainer = styled.div`
  margin-left: 24px;
`;

const SiteGroupsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
  margin-top: 24px;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const GroupContent = styled.div`
  padding: 20px;
`;

const GroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
`;

const GroupTitleSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
`;

const GroupIcon = styled.div`
  color: ${colors.primary.main};
  font-size: 20px;
  margin-top: 2px;
`;

const GroupInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GroupName = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.neutral[800]};
  line-height: 1.4;
`;

const UserBadge = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 8px;
  background: ${colors.info.light};
  border: 1px solid ${colors.info.border};
  border-radius: 12px;
  font-size: 12px;
  color: ${colors.info.main};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.info.main};
    color: white;
  }
`;

const UserTooltipContent = styled.div`
  max-width: 250px;
`;

const TooltipTitle = styled.div`
  font-weight: 600;
  color: white;
  margin-bottom: 6px;
  font-size: 18px;
`;

const UserList = styled.div`
  font-size: 15px;
`;

const UserListItem = styled.div`
  color: white;
  margin-bottom: 2px;
  line-height: 1.3;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
`;

const GroupStats = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${colors.neutral[50]};
  border-radius: 8px;
  border: 1px solid ${colors.neutral[200]};
`;

const StatIcon = styled.div`
  font-size: 18px;
  color: ${colors.primary.main};
`;

const StatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatNumber = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.neutral[800]};
`;

const LastModifiedContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LastModifiedLabel = styled.span`
  font-size: 12px;
  color: ${colors.neutral[500]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const LastModifiedValue = styled.div`
  font-size: 13px;
  color: ${colors.neutral[700]};
`;

const UnassignedSection = styled.div`
  margin-top: 40px;
`;

const AssignedSection = styled.div`
  margin-top: 40px;
`;

const SectionHeader = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
  color: ${colors.neutral[800]};
`;

const SectionSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${colors.neutral[500]};
  line-height: 1.4;
`;

const UnassignedSitesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AssignedSitesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const UnassignedSiteContent = styled.div`
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AssignedSiteContent = styled.div`
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const SiteInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SiteInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const SiteNameUnassigned = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${colors.warning.main};
`;

const SiteNameAssigned = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${colors.info.main};
`;

const SiteLocation = styled.span`
  font-size: 13px;
  color: ${colors.neutral[600]};
`;

const SiteLocationAssigned = styled.span`
  font-size: 13px;
  color: ${colors.neutral[600]};
`;

const SystemsBadge = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 10px;
  background: ${colors.neutral[100]};
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: ${colors.neutral[700]};
  white-space: nowrap;
`;

const GroupTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const GroupTag = styled.span`
  background: ${colors.siteGroup.background};
  color: ${colors.siteGroup.color};
  border: 1px solid ${colors.siteGroup.border};
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  backdrop-filter: blur(10px);
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

const ErrorText = styled.div`
  color: #ff4d4f;
  font-size: 14px;
`;

const RetryButton = styled.button`
  background-color: #ff4d4f;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #ff7875;
  }
`;
